import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL, API_ENDPOINTS } from '../config/api';
import '../styles/RazorpayCheckout.css';

const RazorpayCheckout = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [plan, setPlan] = useState(null);
  const [mobileNumber, setMobileNumber] = useState('');
  const [razorpayKey, setRazorpayKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [paymentStatus, setPaymentStatus] = useState(null);

  const planId = searchParams.get('planId');
  const mobileFromUrl = searchParams.get('mobile');

  // Load plan details and Razorpay key on mount
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        setError('');

        // Get Razorpay key
        const token = sessionStorage.getItem('token');
        const keyResponse = await axios.get(API_ENDPOINTS.PAYMENT_RAZORPAY_KEY, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRazorpayKey(keyResponse.data.key);

        // Get plan details if planId provided
        if (planId) {
          const planResponse = await axios.get(`${API_BASE_URL}/api/plans/${planId}`);
          setPlan(planResponse.data);
        }

        // Set mobile number from URL or previous OTP verification
        if (mobileFromUrl) {
          setMobileNumber(mobileFromUrl);
        }
      } catch (err) {
        console.error('Error loading initial data:', err);
        setError(err.response?.data?.message || 'Failed to load payment details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();

    // Load Razorpay script
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [planId, mobileFromUrl]);

  /**
   * Step 1: Create payment order with backend
   */
  const handleCreateOrder = async () => {
    if (!mobileNumber.trim()) {
      setError('Please enter your mobile number');
      return;
    }

    if (!plan || !planId) {
      setError('No plan selected. Please go back and select a plan.');
      return;
    }

    try {
      setProcessingPayment(true);
      setError('');

      const token = sessionStorage.getItem('token');
      const orderResponse = await axios.post(
        API_ENDPOINTS.PAYMENT_CREATE_ORDER,
        {
          planId: planId,
          mobileNumber: mobileNumber.trim()
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const { order, plan: orderPlan } = orderResponse.data;

      // Step 2: Open Razorpay checkout
      openRazorpayCheckout(order, orderPlan);
    } catch (err) {
      console.error('Error creating order:', err);
      setError(err.response?.data?.message || 'Failed to create payment order.');
      setProcessingPayment(false);
    }
  };

  /**
   * Step 2: Open Razorpay checkout modal
   */
  const openRazorpayCheckout = (order, orderPlan) => {
    if (!window.Razorpay) {
      setError('Razorpay SDK not loaded. Please refresh the page.');
      setProcessingPayment(false);
      return;
    }

    const options = {
      key: razorpayKey,
      amount: order.amount, // Amount in paise
      currency: order.currency,
      order_id: order.id, // Order ID from backend
      name: 'Asset Management System',
      description: `${orderPlan.name} Plan - ${orderPlan.users} users, ${orderPlan.assets} assets`,
      notes: {
        plan_name: orderPlan.name,
        plan_price: orderPlan.price
      },
      prefill: {
        contact: mobileNumber
      },
      theme: {
        color: '#3b82f6'
      },
      handler: function (response) {
        // Step 3: Handle successful payment response
        verifyPaymentSignature(
          order.id,
          response.razorpay_payment_id,
          response.razorpay_signature
        );
      },
      modal: {
        ondismiss: function () {
          setProcessingPayment(false);
          setError('Payment cancelled by user.');
        }
      }
    };

    try {
      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (err) {
      console.error('Error opening Razorpay:', err);
      setError('Failed to open payment checkout.');
      setProcessingPayment(false);
    }
  };

  /**
   * Step 3: Verify payment signature with backend
   */
  const verifyPaymentSignature = async (orderId, paymentId, signature) => {
    try {
      const token = sessionStorage.getItem('token');
      const verifyResponse = await axios.post(
        API_ENDPOINTS.PAYMENT_VERIFY,
        {
          razorpayOrderId: orderId,
          razorpayPaymentId: paymentId,
          razorpaySignature: signature
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Payment successful
      setSuccess('Payment completed successfully! Subscription activated.');
      setPaymentStatus({
        status: 'success',
        paymentId: verifyResponse.data.payment.paymentId,
        orderId: verifyResponse.data.payment.orderId,
        planName: verifyResponse.data.plan.name,
        renewalDate: verifyResponse.data.subscription.renewalDate
      });

      // Redirect after showing success
      setTimeout(() => {
        navigate('/lab-admin/dashboard', { state: { paymentSuccess: true } });
      }, 3000);
    } catch (err) {
      console.error('Error verifying payment:', err);
      setError(
        err.response?.data?.message ||
        'Payment verification failed. Please contact support if amount is deducted.'
      );
      setPaymentStatus({
        status: 'verification_failed',
        message: err.response?.data?.message
      });
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="razorpay-checkout-page">
        <div className="checkout-card">
          <div className="loading-spinner"></div>
          <p>Loading payment details...</p>
        </div>
      </div>
    );
  }

  if (paymentStatus?.status === 'success') {
    return (
      <div className="razorpay-checkout-page">
        <div className="checkout-card success-card">
          <div className="success-icon">✓</div>
          <h2>Payment Successful!</h2>
          <p>Your subscription has been activated.</p>
          <div className="payment-details">
            <div className="detail-row">
              <span className="label">Plan:</span>
              <span className="value">{paymentStatus.planName}</span>
            </div>
            <div className="detail-row">
              <span className="label">Payment ID:</span>
              <span className="value">{paymentStatus.paymentId}</span>
            </div>
            <div className="detail-row">
              <span className="label">Renewal Date:</span>
              <span className="value">{new Date(paymentStatus.renewalDate).toLocaleDateString()}</span>
            </div>
          </div>
          <p className="redirect-message">Redirecting to dashboard...</p>
        </div>
      </div>
    );
  }

  if (paymentStatus?.status === 'verification_failed') {
    return (
      <div className="razorpay-checkout-page">
        <div className="checkout-card error-card">
          <div className="error-icon">✕</div>
          <h2>Payment Verification Failed</h2>
          <p className="error-message">{paymentStatus.message}</p>
          <p className="support-message">
            If amount has been deducted, please contact support with your Payment ID.
          </p>
          <div className="actions">
            <button
              className="primary-btn"
              onClick={() => {
                setPaymentStatus(null);
                setError('');
              }}
            >
              Try Again
            </button>
            <button className="secondary-btn" onClick={() => navigate('/admin/buy-plan')}>
              Back to Plan Selection
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="razorpay-checkout-page">
      <div className="checkout-card">
        <h2>Complete Your Payment</h2>
        <p>Secure payment gateway powered by Razorpay</p>

        {error && <div className="message error">{error}</div>}
        {success && <div className="message success">{success}</div>}

        {/* Plan Details */}
        {plan ? (
          <div className="plan-summary">
            <h3>Plan Summary</h3>
            <div className="summary-item">
              <span className="label">Plan:</span>
              <span className="value">{plan.name}</span>
            </div>
            <div className="summary-item">
              <span className="label">Price:</span>
              <span className="value price">₹{Number(plan.price || 0).toFixed(2)}/month</span>
            </div>
            <div className="summary-item">
              <span className="label">Users:</span>
              <span className="value">{plan.users}</span>
            </div>
            <div className="summary-item">
              <span className="label">Assets:</span>
              <span className="value">{plan.assets}</span>
            </div>
            {plan.description && (
              <div className="summary-item">
                <span className="label">Description:</span>
                <span className="value">{plan.description}</span>
              </div>
            )}
          </div>
        ) : (
          <div className="info">No plan selected.</div>
        )}

        {/* Mobile Number Input */}
        <div className="form-group">
          <label htmlFor="mobile">Contact Mobile Number</label>
          <input
            id="mobile"
            type="tel"
            value={mobileNumber}
            onChange={(e) => setMobileNumber(e.target.value)}
            placeholder="e.g. +919876543210"
            maxLength={16}
            disabled={processingPayment}
          />
          <small>This number will be used for payment confirmation</small>
        </div>

        {/* Payment Button */}
        <div className="actions">
          <button
            className="primary-btn pay-button"
            onClick={handleCreateOrder}
            disabled={processingPayment || !plan || !razorpayKey}
          >
            {processingPayment ? (
              <>
                <span className="spinner"></span> Processing...
              </>
            ) : (
              `Pay ₹${Number(plan?.price || 0).toFixed(2)}`
            )}
          </button>
          <button
            className="secondary-btn"
            onClick={() => navigate('/admin/buy-plan')}
            disabled={processingPayment}
          >
            Back to Plan Selection
          </button>
        </div>

        {/* Security Notice */}
        <div className="security-notice">
          <p>🔒 Your payment is encrypted and secure. Razorpay is PCI-DSS compliant.</p>
        </div>

        {/* Supported Payment Methods */}
        <div className="payment-methods">
          <p>Accepted Payment Methods:</p>
          <div className="methods-list">
            <span className="method">💳 Credit/Debit Cards</span>
            <span className="method">🏦 Net Banking</span>
            <span className="method">📱 UPI</span>
            <span className="method">🔗 Digital Wallets</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RazorpayCheckout;
